/**
 * The reader (the prototype's `reader()`): phone bar (Back · folder · Details), source line with time,
 * title, byline (author or source · reading time · previous / next), the article, and the end line.
 * The heading renders from the list row immediately; the body follows from `messages.get`.
 */

import { useLayoutEffect, useRef } from 'react';
import type { MessageDetail, MessageRow } from '@digester/core';
import { Icon } from '../icons/Icon.js';
import { sourceKindIcon } from '../ui/source-icons.js';
import { Tool } from '../ui/Tool.js';
import { ArticleBody } from './ArticleBody.js';
import type { Lookups } from './data.js';
import { readerTime, headingOf } from './format.js';
import { restoreAndRememberScroll } from './scroll-memory.js';

export interface ReaderPaneProps {
  row: MessageRow | null;
  detail: MessageDetail | undefined;
  detailError: Error | null;
  lookups: Lookups;
  showImages: boolean;
  timeZone: string | undefined;
  scrollKey: string;
  onBack(): void;
  onDetails(): void;
  onPrevious(): void;
  onNext(): void;
  onOpenSource(id: string): void;
}

function httpUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

export function ReaderPane({ row, detail, detailError, lookups, showImages, timeZone, scrollKey, onBack, onDetails, onPrevious, onNext, onOpenSource }: ReaderPaneProps) {
  const ref = useRef<HTMLElement>(null);
  const loaded = detail !== undefined && detail.message.id === row?.id;

  // Scroll: remembered per message; a newly opened message starts at the top.
  // (Images and the article arrive after the first paint: restore once the content is tall enough.)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    return restoreAndRememberScroll(el, scrollKey, 'reader');
  }, [scrollKey, loaded]);

  if (!row) {
    return (
      <main className="reader" ref={ref}>
        <div className="empty">
          <h2>Select a message</h2>
        </div>
      </main>
    );
  }

  const source = lookups.source(row.sourceId);
  const folder = lookups.folder(row.folderId);
  const message = loaded ? detail.message : null;
  const original = httpUrl(message?.url ?? null);
  const tagNames = (message?.tagIds ?? row.tagIds).map((id) => lookups.tag(id)?.name).filter((x): x is string => typeof x === 'string');

  return (
    <main className="reader" ref={ref} aria-label="Reader">
      <div className="reader-mobile-bar">
        <Tool icon="arrow-left" label="Back to messages" onClick={onBack} />
        <span>{folder?.name ?? ''}</span>
        <Tool icon="panel-right" label="Details" onClick={onDetails} />
      </div>
      <div className="reader-heading">
        <div className="source-line">
          <button type="button" onClick={() => onOpenSource(row.sourceId)}>
            <Icon name={sourceKindIcon(source?.kind ?? 'rss')} />
            {source?.name ?? 'Source'}
          </button>
          <time dateTime={new Date(row.sortAt).toISOString()}>{readerTime(row.sortAt, timeZone)}</time>
        </div>
        <h1>{headingOf(row)}</h1>
        <div className="byline">
          <span>{row.author ?? source?.name ?? ''}</span>
          <span>·</span>
          <span>{row.readingMinutes} min read</span>
          <div className="reader-navigation">
            <Tool icon="chevron-left" label="Previous message · K" onClick={onPrevious} />
            <Tool icon="chevron-right" label="Next message · J" onClick={onNext} />
          </div>
        </div>
      </div>
      {message ? (
        <ArticleBody message={message} showImages={showImages} tagNames={tagNames} />
      ) : detailError ? (
        <div className="article-body">
          <p className="subtle" role="alert">
            {detailError.message}
          </p>
        </div>
      ) : (
        <div className="article-body" aria-busy="true">
          <p>{row.excerpt}</p>
        </div>
      )}
      <footer className="article-end">
        <span>
          End of message
          {original ? (
            <>
              {' · '}
              <a className="article-original" href={original} target="_blank" rel="noopener noreferrer nofollow">
                Open original
              </a>
            </>
          ) : null}
        </span>
        <button type="button" onClick={onNext}>
          Next message <Icon name="chevron-right" />
        </button>
      </footer>
    </main>
  );
}
